import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NutriScore from '../components/NutriScore';
import { useApp } from '../context/AppContext';
import { TAGS } from '../mock';
import { SHOW_PRICES } from '../config';
import { mealNutrition, mealKcal, formatNutrient, NUTRITION_KEYS } from '../lib/nutrition';
import { ArrowLeft, Plus, Minus, ShoppingBag, Check } from 'lucide-react';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getDish, tf, t, lang, addToCart, menuState } = useApp();
  const dish = getDish(id);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  if (!dish) {
    return (
      <div className="min-h-screen bg-[#faf9f5]">
        <Header />
        <div className="max-w-[900px] mx-auto px-5 py-24 text-center">
          <p className="text-neutral-600 mb-4">404</p>
          <Link to="/" className="text-[#4b5e34] font-medium">{t('back')}</Link>
        </div>
      </div>
    );
  }

  // All nutrition arithmetic lives in lib/nutrition.js — the supplier publishes
  // per portion and pack labels are per 100 g, and mixing the two is what put
  // 322 g of protein on this page. Do not scale by weight here.
  const nutri = mealNutrition(dish);
  const rows = NUTRITION_KEYS
    .filter((k) => nutri.values[k] !== undefined)
    .map((k) => [k, formatNutrient(k, nutri.values[k])]);
  const nutritionScope =
    nutri.scope === '100g' ? t('per_100g')
      : nutri.weight ? `${t('per_meal_of')} ${Math.round(nutri.weight)} g`
        : t('per_meal');
  const displayKcal = mealKcal(dish);
  const isPreorder = dish.availability === 'week2';
  // Go back to the tab this dish came from rather than using history: this also
  // works for someone who arrived from a shared link or the printed QR code,
  // where there is no previous page to return to.
  const backTab =
    dish.availability === 'week2' ? 'week2'
      : dish.availability === 'week1' ? 'week'
        : ['entrees', 'desserts', 'boissons'].includes(dish.category) ? dish.category : 'week';
  const backLabel =
    backTab === 'week2' ? t('back_to_week2')
      : backTab === 'week' ? t('back_to_week')
        : t('back');
  const orderable = isPreorder && menuState.open;
  const isDrink = dish.category === 'boissons';

  const handleAdd = () => {
    addToCart(dish.id, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <div className="min-h-screen bg-[#faf9f5]">
      <Header />
      <div className="max-w-[1100px] mx-auto px-5 py-8">
        <button
          onClick={() => navigate(backTab === 'week' ? '/' : `/?tab=${backTab}`)}
          data-testid="pd-back"
          className="flex items-center gap-2 text-sm text-neutral-600 hover:text-[#4b5e34] mb-6 transition-colors"
        >
          <ArrowLeft size={16} /> {backLabel}
        </button>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          <div className="relative rounded-3xl overflow-hidden bg-white border border-neutral-200 shadow-sm">
            <img src={dish.image} alt={tf(dish.name)} className="w-full aspect-square object-cover" />
            {dish.nutriScore && !['entrees', 'desserts'].includes(dish.category) && <div className="absolute top-4 right-4"><NutriScore score={dish.nutriScore} size={46} /></div>}
          </div>

          <div>
            {dish.brand && <p className="text-xs font-semibold uppercase tracking-widest text-[#b8863b] mb-2">{dish.brand}</p>}
            <h1 className="font-display text-[34px] leading-tight text-[#2f3826] mb-3">{tf(dish.name)}</h1>
            <div className="flex flex-wrap gap-2 mb-4">
              {(dish.tags || []).map((tag) => (
                <span key={tag} className="text-xs font-semibold text-[#4b5e34] bg-[#eef0e6] px-2.5 py-1 rounded-full">
                  {(TAGS[tag] && TAGS[tag][lang]) || tag}
                </span>
              ))}
            </div>
            <p className="text-neutral-600 leading-relaxed mb-5">{tf(dish.description)}</p>

            <div className="flex items-center gap-6 text-sm text-neutral-600 mb-6">
              {displayKcal ? <span><b className="text-[#2f3826]">{displayKcal}</b> kcal</span> : null}
              {dish.weight && <span><b className="text-[#2f3826]">{dish.weight}</b> g</span>}
            </div>

            {SHOW_PRICES && (
              <div className="flex items-center gap-4 mb-6">
                <span className="font-display text-3xl text-[#4b5e34]">CHF {dish.price?.toFixed(2)}</span>
              </div>
            )}

            {orderable ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-neutral-300 rounded-lg overflow-hidden">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5 hover:bg-neutral-100"><Minus size={16} /></button>
                  <span className="w-10 text-center font-semibold">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="p-2.5 hover:bg-neutral-100"><Plus size={16} /></button>
                </div>
                <button onClick={handleAdd} data-testid="pd-add-to-cart" className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium text-white transition-colors ${added ? 'bg-[#5a8a3a]' : 'bg-[#4b5e34] hover:bg-[#3f5230]'}`}>
                  {added ? <><Check size={18} /> {t('added')}</> : <><ShoppingBag size={18} /> {t('add_to_cart')}</>}
                </button>
              </div>
            ) : isPreorder ? (
              <div data-testid="pd-preorder-closed" className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl p-4">
                <ShoppingBag size={17} className="mt-0.5 shrink-0" />
                <span>{t('week2_closed_note')}</span>
              </div>
            ) : (
              <div data-testid="pd-in-fridge-note" className="flex items-start gap-2 bg-[#eef0e6] text-[#2f3826] text-sm rounded-xl p-4">
                <ShoppingBag size={17} className="text-[#4b5e34] mt-0.5 shrink-0" />
                <span>{t('in_fridge_note')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Nutrition + ingredients */}
        <div className="grid md:grid-cols-2 gap-8 mt-14">
          {!isDrink && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-6">
            <div className="flex items-baseline justify-between gap-3 mb-4">
              <h3 className="font-display text-xl text-[#2f3826]">{t('nutrition_values')}</h3>
              {rows.length > 0 && <span data-testid="pd-nutrition-scope" className="text-xs font-medium text-neutral-500 whitespace-nowrap">{nutritionScope}</span>}
            </div>
            {rows.length === 0 ? (
              <p data-testid="pd-nutrition-empty" className="text-sm text-neutral-500 leading-relaxed">{t('nutrition_unavailable')}</p>
            ) : (
              <>
                <table className="w-full text-sm" data-testid="pd-nutrition-table">
                  <tbody>
                    {rows.map(([k, v], i) => (
                      <tr key={k} className={`${i % 2 ? '' : 'bg-neutral-50'} ${k === 'sat' || k === 'sugar' ? 'text-neutral-500' : 'text-neutral-800'}`}>
                        <td className={`py-2 px-3 ${k === 'sat' || k === 'sugar' ? 'pl-6' : 'font-medium'}`}>{t(k)}</td>
                        <td className="py-2 px-3 text-right">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
          )}
          <div className="bg-white rounded-2xl border border-neutral-200 p-6">
            <h3 className="font-display text-xl text-[#2f3826] mb-4">{t('ingredients')}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{tf(dish.ingredients)}</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
